import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  CheckCircle,
  Warning,
  Eraser,
  PenNib,
  FileText,
  Clock,
  MapPin,
  X
} from '@phosphor-icons/react';


export default function QuoteSignPage() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [autoFlow, setAutoFlow] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [actionChoice, setActionChoice] = useState(null); // 'sign' | 'changes' | null
  const [changesMsg, setChangesMsg] = useState('');
  const [sendingChanges, setSendingChanges] = useState(false);
  const [changesSent, setChangesSent] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  useEffect(() => {
    if (quote && !quote.signed_at && canvasRef.current) {
      initCanvas();
    }
  }, [quote]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/public/quote/${quoteId}`);
      setQuote(res.data);
      if (res.data.signed_at) {
        setSigned(true);
        // Restore auto-flow data from quote if page was reloaded
        if (res.data.auto_payment_checkout_url) {
          setAutoFlow({
            payment_link_created: true,
            checkout_url: res.data.auto_payment_checkout_url,
            invoice_number: res.data.auto_invoice_number,
            deposit_amount: res.data.auto_deposit_amount,
          });
        }
      }
    } catch (err) {
      setError('Cotizacion no encontrada o link invalido');
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Draw baseline
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#cbd5e1';
    ctx.beginPath();
    ctx.moveTo(20, 130);
    ctx.lineTo(canvas.width - 20, 130);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1e293b';
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    setHasDrawn(false);
    initCanvas();
  };

  const handleSign = async () => {
    if (!signerName.trim()) {
      alert('Por favor ingresa tu nombre completo');
      return;
    }
    if (!hasDrawn) {
      alert('Por favor dibuja tu firma en el recuadro');
      return;
    }

    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureImage = canvas.toDataURL('image/png');

      await axios.post(`${API_URL}/api/public/quote/${quoteId}/sign`, {
        name: signerName.trim(),
        signature: signatureImage,
      }).then(res => {
        setAutoFlow(res.data.auto_flow || null);
      });

      setSigned(true);
      fetchQuote();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al firmar. Intenta de nuevo.');
    } finally {
      setSigning(false);
    }
  };

  const handleRequestChanges = async () => {
    const trimmedName = signerName.trim();
    if (!trimmedName) {
      alert('Por favor ingresa tu nombre completo.');
      return;
    }
    if (!changesMsg.trim() || changesMsg.trim().length < 10) {
      alert('Por favor describe los cambios solicitados (minimo 10 caracteres).');
      return;
    }
    setSendingChanges(true);
    try {
      await axios.post(`${API_URL}/api/public/quote/${quoteId}/request-changes`, {
        name: trimmedName,
        message: changesMsg.trim(),
      });
      setChangesSent(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al enviar la solicitud. Intenta de nuevo.');
    } finally {
      setSendingChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <CircleNotch size={48} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <Warning size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Error</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const isExpired = new Date(quote.expires_at) < new Date();
  const canSign = quote.status === 'Sent' || quote.status === 'Approved';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-[#0055FF] text-white py-6 px-4 text-center">
        <img
          src="https://customer-assets.emergentagent.com/job_0246dde3-e1c1-4ddd-b1f0-cbdadc084ea8/artifacts/indb97if_WhatsApp%20Image%202026-02-08%20at%208.04.43%20PM%20%281%29.jpeg"
          alt="IQES Logo"
          className="w-14 h-14 rounded-xl object-contain bg-white p-1 mx-auto mb-3"
        />
        <h1 className="text-xl sm:text-2xl font-bold">IQES LLC</h1>
        <p className="text-blue-200 text-sm">Low Voltage Solutions</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="quote-sign-page">
        {/* Success State */}
        {signed && (
          <div className="space-y-4" data-testid="signature-success">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
              <CheckCircle size={56} className="text-green-500 mx-auto mb-3" weight="fill" />
              <h2 className="text-xl font-bold text-green-800">Cotizacion Firmada</h2>
              <p className="text-green-600 mt-2">
                Firmada por: <strong>{quote.signature_name}</strong>
              </p>
              <p className="text-green-600 text-sm mt-1">
                {quote.signed_at && new Date(quote.signed_at).toLocaleString('es-ES')}
              </p>
            </div>

            {/* Auto Flow - Payment Link */}
            {autoFlow?.payment_link_created && autoFlow.checkout_url && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center" data-testid="auto-payment-section">
                <h3 className="text-lg font-bold text-blue-800 mb-2">Siguiente Paso: Pago del Anticipo</h3>
                <div className="bg-white rounded-xl p-4 mb-4 border border-blue-100">
                  <p className="text-sm text-slate-600 mb-1">Factura: <strong>{autoFlow.invoice_number}</strong></p>
                  <p className="text-sm text-slate-600 mb-1">Total del proyecto: <strong>${quote.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></p>
                  <p className="text-2xl font-bold text-blue-600 mt-2">
                    Anticipo: ${autoFlow.deposit_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">50% del total del proyecto</p>
                </div>
                <a
                  href={autoFlow.checkout_url}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-green-600 text-white rounded-xl font-bold text-base hover:bg-green-700 transition-colors"
                  data-testid="pay-deposit-btn"
                >
                  Pagar Anticipo Ahora
                </a>
                <p className="text-xs text-slate-500 mt-3">
                  Tambien le enviamos el link de pago a su email.
                </p>
              </div>
            )}

            {!autoFlow?.payment_link_created && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                <p className="text-slate-500 text-sm">
                  El equipo de IQES LLC se pondra en contacto para coordinar el inicio del proyecto.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Quote Details */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-blue-600" />
                  <h2 className="font-bold text-lg text-slate-900">Cotizacion #{quote.quote_number}</h2>
                </div>
                <p className="text-sm text-slate-500 mt-1">{quote.client_name}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                quote.status === 'Signed' ? 'bg-green-100 text-green-700' :
                isExpired ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {quote.status === 'Signed' ? 'Firmada' : isExpired ? 'Vencida' : 'Pendiente de Firma'}
              </span>
            </div>
          </div>

          {/* Project Info */}
          {quote.project_address && (
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <MapPin size={16} className="text-slate-400" />
                {quote.project_address}, {quote.project_city} {quote.project_state} {quote.project_zip}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-4 text-sm text-slate-500 flex-wrap">
            <span className="flex items-center gap-1">
              <Clock size={14} />
              Creada: {new Date(quote.created_at).toLocaleDateString('es-ES')}
            </span>
            <span className={`flex items-center gap-1 ${isExpired ? 'text-red-500 font-semibold' : ''}`}>
              <Clock size={14} />
              {isExpired ? 'Vencida' : 'Valida hasta'}: {new Date(quote.expires_at).toLocaleDateString('es-ES')}
            </span>
          </div>

          {/* Items Table */}
          <div className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3 text-sm">Servicios / Services</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quote-items-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-slate-500 font-medium">Descripcion</th>
                    <th className="text-center py-2 text-slate-500 font-medium">Cant.</th>
                    <th className="text-right py-2 text-slate-500 font-medium">Precio</th>
                    <th className="text-right py-2 text-slate-500 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.items.map((item, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2.5 text-slate-700">{item.description}</td>
                      <td className="py-2.5 text-center text-slate-600">{item.quantity}</td>
                      <td className="py-2.5 text-right text-slate-600">${item.unit_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="py-2.5 text-right font-medium text-slate-900">${item.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-4 border-t border-slate-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="text-slate-700">${quote.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              {quote.tax_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tax ({quote.tax_percent}%)</span>
                  <span className="text-slate-700">${quote.tax_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
                <span className="text-slate-900">TOTAL</span>
                <span className="text-blue-600">${quote.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Terminos de Pago:</strong> {quote.payment_terms}
              </p>
            </div>

            {/* Labor Only Disclaimer */}
            <div
              className="mt-3 p-4 bg-red-50 border-2 border-red-300 rounded-lg"
              data-testid="labor-only-disclaimer"
            >
              <p className="text-sm font-bold text-red-800 mb-1">
                AVISO IMPORTANTE — SOLO MANO DE OBRA
              </p>
              <p className="text-xs text-red-700 leading-relaxed">
                Este estimado incluye <strong>UNICAMENTE mano de obra</strong>.
                NO incluye materiales, equipos, accesorios, permisos, inspecciones,
                impuestos ni tarifas de servicios publicos. Cualquier material
                o alcance adicional sera cotizado por separado y requiere
                aprobacion previa del cliente.
              </p>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600"><strong>Notas:</strong> {quote.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Section - TWO CLEAR PATHS */}
        {canSign && !signed && !isExpired && !changesSent && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="action-section">
            {/* Step 1: Choose action */}
            {!actionChoice && (
              <div className="p-5">
                <h3 className="text-center font-bold text-slate-900 mb-1">Que deseas hacer?</h3>
                <p className="text-center text-sm text-slate-500 mb-5">Elige una opcion para continuar</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setActionChoice('sign')}
                    className="flex flex-col items-center gap-2 p-5 bg-green-50 border-2 border-green-500 rounded-xl hover:bg-green-100 transition-colors"
                    data-testid="choose-sign-btn"
                  >
                    <CheckCircle size={36} className="text-green-600" weight="fill" />
                    <span className="font-bold text-green-800 text-base">Acepto y Firmo</span>
                    <span className="text-xs text-green-700 text-center leading-snug">
                      Estoy de acuerdo con la cotizacion. Firmo y pago el anticipo.
                    </span>
                  </button>
                  <button
                    onClick={() => setActionChoice('changes')}
                    className="flex flex-col items-center gap-2 p-5 bg-orange-50 border-2 border-orange-500 rounded-xl hover:bg-orange-100 transition-colors"
                    data-testid="choose-changes-btn"
                  >
                    <PenNib size={36} className="text-orange-600" />
                    <span className="font-bold text-orange-800 text-base">Solicitar Cambios</span>
                    <span className="text-xs text-orange-700 text-center leading-snug">
                      Necesito ajustes. Pido una cotizacion actualizada (sin firma).
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2A: Sign flow */}
            {actionChoice === 'sign' && (
              <div data-testid="signature-section">
                <div className="p-5 border-b border-slate-100 bg-green-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PenNib size={22} className="text-green-700" />
                    <h3 className="font-bold text-slate-900">Firmar y Pagar</h3>
                  </div>
                  <button
                    onClick={() => { setActionChoice(null); setHasDrawn(false); }}
                    className="text-xs text-slate-500 hover:text-slate-800"
                    data-testid="back-to-choice-btn"
                  >
                    Cambiar opcion
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Completo *</label>
                    <input
                      type="text"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Ingresa tu nombre completo"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                      data-testid="signer-name-input"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-slate-700">Tu Firma *</label>
                      <button onClick={clearCanvas} className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-600" data-testid="clear-signature-btn">
                        <Eraser size={14} />Limpiar
                      </button>
                    </div>
                    <div ref={containerRef} className="border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-white cursor-crosshair touch-none" data-testid="signature-canvas-container">
                      <canvas
                        ref={canvasRef}
                        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                        className="w-full" style={{ touchAction: 'none' }} data-testid="signature-canvas"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Dibuja tu firma con el mouse o el dedo</p>
                  </div>
                  <button
                    onClick={handleSign}
                    disabled={signing || !hasDrawn || !signerName.trim()}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold text-base hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-signature-btn"
                  >
                    {signing ? <CircleNotch size={20} className="animate-spin" /> : <PenNib size={20} />}
                    {signing ? 'Firmando...' : 'Firmar y Continuar al Pago'}
                  </button>
                  <p className="text-xs text-slate-400 text-center">
                    Al firmar, aceptas los terminos. El siguiente paso sera pagar el anticipo del 50%.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2B: Request Changes flow (no signature needed) */}
            {actionChoice === 'changes' && (
              <div data-testid="request-changes-form">
                <div className="p-5 border-b border-slate-100 bg-orange-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PenNib size={22} className="text-orange-700" />
                    <h3 className="font-bold text-slate-900">Solicitar Cambios</h3>
                  </div>
                  <button
                    onClick={() => { setActionChoice(null); setChangesMsg(''); }}
                    className="text-xs text-slate-500 hover:text-slate-800"
                    data-testid="back-to-choice-btn-2"
                  >
                    Cambiar opcion
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                    <strong>Importante:</strong> No necesitas firmar. Nuestro equipo recibira tu solicitud,
                    ajustara la cotizacion y te enviara una <strong>nueva version actualizada</strong> por correo
                    para tu revision y firma.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tu Nombre *</label>
                    <input
                      type="text"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Ingresa tu nombre completo"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                      data-testid="changes-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Cambios solicitados *</label>
                    <textarea
                      rows={5}
                      value={changesMsg}
                      onChange={(e) => setChangesMsg(e.target.value)}
                      placeholder="Ej: Por favor reducir cantidad de puntos Cat6 a 3. Necesito agregar 2 OTDR adicionales. Cambiar fecha programada al 15 de julio..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                      data-testid="changes-message-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Minimo 10 caracteres. Se enviara al equipo IQES.</p>
                  </div>
                  <button
                    onClick={handleRequestChanges}
                    disabled={sendingChanges || !signerName.trim() || changesMsg.trim().length < 10}
                    className="w-full px-6 py-3 bg-orange-600 text-white rounded-xl font-semibold text-base hover:bg-orange-700 transition-colors disabled:opacity-50"
                    data-testid="send-changes-btn"
                  >
                    {sendingChanges ? 'Enviando solicitud...' : 'Enviar Solicitud de Cambios'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Changes Sent Success */}
        {changesSent && (
          <div className="bg-green-50 border border-green-300 rounded-2xl p-6 text-center" data-testid="changes-sent-success">
            <CheckCircle size={56} className="text-green-600 mx-auto mb-3" weight="fill" />
            <h2 className="text-xl font-bold text-green-800">Solicitud Enviada</h2>
            <p className="text-sm text-green-700 mt-2 max-w-md mx-auto">
              Nuestro equipo recibira tus comentarios y te enviara una <strong>cotizacion actualizada</strong> por
              correo electronico. No necesitas hacer nada mas en este momento.
            </p>
          </div>
        )}

        {/* Expired Notice */}
        {isExpired && !signed && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <Warning size={40} className="text-red-500 mx-auto mb-2" />
            <h3 className="font-bold text-red-800">Cotizacion Vencida</h3>
            <p className="text-sm text-red-600 mt-1">
              Esta cotizacion ha vencido. Contacta a IQES LLC para una nueva cotizacion.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6 mt-4 border-t border-slate-200">
          <p className="text-sm font-bold text-slate-800">IQES LLC</p>
          <p className="text-xs text-slate-600 font-semibold mt-1">Innovation | Quality | Engineering | Services</p>
          <p className="text-xs text-slate-500 italic mt-1.5 max-w-md mx-auto">
            &ldquo;Building Strategic Partnerships Through Innovation, Quality, Engineering, and Service.&rdquo;
          </p>
          <p className="text-xs text-slate-500 mt-3">
            255 S Orange Avenue, Suite 104 #1905, Orlando, FL 32801 | 786-436-7307
          </p>
          <p className="text-xs text-slate-500 mt-1">info@iqeslowvoltage.com | www.iqeslowvoltage.com</p>
        </div>
      </div>
    </div>
  );
}
